import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getInvoice,
  getInvoiceSyncState,
  listInvoices,
  updateInvoiceCompany,
  updateInvoiceService,
  updateKbInvoice,
  type InvoiceFilters,
  type UpdateKbInvoiceInput,
  updateInvoiceProject,
} from '../lib/invoicesApi';
import { useActiveCompanyId } from '../store/useCompanyStore';

// Invoices are markdown-first (CLE-41): create/delete/mark-paid happen through
// the AI assistant (KB markdown -> sync). The type/status/project edit (CLE-77)
// is the one curated direct-edit path, also markdown-first via /api/kb/invoices.

const INVOICES_KEY = ['invoices'] as const;

export function useInvoices(filters: InvoiceFilters = {}) {
  // Global firm switcher (multi-company): threaded internally so pages don't
  // have to pass it. An explicit filters.companyId wins.
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  const merged: InvoiceFilters = { companyId: activeCompanyId, ...filters };
  return useQuery({
    queryKey: [...INVOICES_KEY, merged],
    queryFn: () => listInvoices(merged),
    placeholderData: (prev) => prev,
  });
}

/**
 * Keeps the invoice lists fresh while the SPV scheduler runs in the background
 * (every 30 minutes by default). Polls the cheap sync-state endpoint once a
 * minute and, when a new run has finished since the last poll, invalidates the
 * invoices / SPV / dashboard queries so the open page refetches on its own.
 * Returns the state for a "sincronizat la HH:mm" hint.
 */
export function useInvoiceSyncWatcher() {
  const qc = useQueryClient();
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  const query = useQuery({
    queryKey: ['invoices-sync-state', activeCompanyId ?? null],
    queryFn: () => getInvoiceSyncState(activeCompanyId),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
    retry: false,
  });
  const lastRunAt = query.data?.lastRunAt ?? null;
  const seen = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (seen.current === undefined) {
      // First observation: the lists were just fetched, nothing to refresh.
      seen.current = lastRunAt;
      return;
    }
    if (lastRunAt === seen.current) return;
    seen.current = lastRunAt;
    if (!lastRunAt) return;
    void qc.invalidateQueries({ queryKey: INVOICES_KEY });
    void qc.invalidateQueries({ queryKey: ['efactura', 'messages'] });
    void qc.invalidateQueries({ queryKey: ['efactura', 'message'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  }, [lastRunAt, qc]);
  return query;
}

export function useInvoice(id: string | undefined) {
  return useQuery({
    queryKey: [...INVOICES_KEY, 'detail', id],
    queryFn: () => getInvoice(id as string),
    enabled: Boolean(id),
  });
}

// Edit an invoice's type/status/project (CLE-77). The PATCH re-derives the DB
// from markdown, so refresh both the list and any open detail view on success.
export function useUpdateKbInvoice(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateKbInvoiceInput) => updateKbInvoice(invoiceId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INVOICES_KEY });
    },
  });
}

// Set/clear the DB-only service tag (services nomenclature). Separate from the
// KB PATCH above because it hits /api/invoices/:id/service directly.
export function useUpdateInvoiceService(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (serviceId: string | null) => updateInvoiceService(invoiceId, serviceId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INVOICES_KEY });
    },
  });
}

// Set/clear the DB-only firm tag (multi-company). NULL = default firm.
export function useUpdateInvoiceProject(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string | null) => updateInvoiceProject(invoiceId, projectId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INVOICES_KEY });
      // The project's Resurse tab lists its invoices from the same rows.
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUpdateInvoiceCompany(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string | null) => updateInvoiceCompany(invoiceId, companyId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INVOICES_KEY });
    },
  });
}
